// Notification service for MoZo.Live
import { supabase } from './supabase.js';
import { retryOperation } from './supabase-service';
import { v4 as uuidv4 } from 'uuid';

// Collection name for notifications
const NOTIFICATIONS_TABLE = 'notifications';

export const notificationService = {
  // Create a new notification
  async createNotification(
    recipientUserId: string,
    type: string,
    senderUserId?: string | null,
    contentPreview?: string | null,
    relatedEntityType?: string | null,
    relatedEntityId?: string | null
  ) {
    return retryOperation(async () => {
      const notificationData = {
        id: uuidv4(),
        recipient_user_id: recipientUserId,
        sender_user_id: senderUserId,
        type,
        content_preview: contentPreview,
        related_entity_type: relatedEntityType,
        related_entity_id: relatedEntityId,
        is_read: false,
        created_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from(NOTIFICATIONS_TABLE)
        .insert(notificationData)
        .select()
        .single();

      if (error) {
        console.error('Error creating notification:', error);
        throw error;
      }

      return data;
    });
  },

  // Get notifications for the current user
  async getUserNotifications(limit = 20, offset = 0, unreadOnly = false) {
    return retryOperation(async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User must be logged in to get notifications');
      }
      
      let query = supabase
        .from(NOTIFICATIONS_TABLE)
        .select(`
          *,
          sender:sender_user_id(
            id,
            username,
            avatar_url,
            verified
          )
        `)
        .eq('recipient_user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(limit)
        .range(offset, offset + limit - 1);
        
      if (unreadOnly) {
        query = query.eq('is_read', false);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error getting notifications:', error);
        throw error;
      }
      
      return data;
    });
  },

  // Mark notification as read
  async markAsRead(notificationId: string) {
    return retryOperation(async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User must be logged in to mark notification as read');
      }
      
      const { data, error } = await supabase
        .from(NOTIFICATIONS_TABLE)
        .update({ is_read: true })
        .eq('id', notificationId)
        .eq('recipient_user_id', user.id) // Security check
        .select()
        .single();
      
      if (error) {
        console.error('Error marking notification as read:', error);
        throw error;
      }
      
      return data;
    });
  },

  // Mark all notifications as read
  async markAllAsRead() {
    return retryOperation(async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User must be logged in to mark all notifications as read');
      }
      
      const { data, error } = await supabase
        .from(NOTIFICATIONS_TABLE)
        .update({ is_read: true })
        .eq('recipient_user_id', user.id)
        .eq('is_read', false)
        .select();
      
      if (error) {
        console.error('Error marking all notifications as read:', error);
        throw error;
      }
      
      return data;
    });
  },

  // Get unread notification count
  async getUnreadCount() {
    return retryOperation(async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('User must be logged in to get unread notification count');
      }
      
      const { count, error } = await supabase
        .from(NOTIFICATIONS_TABLE)
        .select('*', { count: 'exact', head: true })
        .eq('recipient_user_id', user.id)
        .eq('is_read', false);
      
      if (error) {
        console.error('Error getting unread notification count:', error);
        throw error;
      }
      
      return count || 0;
    });
  }
};

export default notificationService;
